import { useMemo, useRef, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api from "@/lib/api";
import { toast } from "sonner";

const Inline = Quill.import("blots/inline");
class CtaBlot extends Inline {
  static blotName = "ctabtn";
  static tagName = "A";
  static className = "dcs-cta";
  static create(value) {
    const node = super.create();
    node.setAttribute("href", value);
    node.setAttribute("rel", "noopener noreferrer");
    node.setAttribute("target", "_blank");
    return node;
  }
  static formats(node) { return node.getAttribute("href"); }
}
Quill.register(CtaBlot, true);

const FORMATS = ["header", "bold", "italic", "underline", "strike", "color", "list", "link", "blockquote", "image", "align", "ctabtn"];

// Quill renders an empty document as "<p><br></p>"; treat that (and blank) as empty.
export function isQuillEmpty(html) {
  if (!html) return true;
  return html.replace(/<(p|br|div|span)[^>]*>/gi, "").replace(/<\/(p|div|span)>/gi, "").replace(/&nbsp;|\s/g, "") === "";
}

export function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
}

const _esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Seed the editor: pass through existing HTML, or convert plain text (with \n) to paragraphs.
export function toEditorHtml(text) {
  if (!text) return "";
  if (/<[a-z][\s\S]*>/i.test(text)) return text;
  return text.split("\n").map((l) => (l.trim() === "" ? "<p><br></p>" : `<p>${_esc(l)}</p>`)).join("");
}

// Resolve a YouTube/Vimeo URL to an email-safe { thumb, href } (clickable thumbnail preview).
async function _resolveVideo(url) {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { thumb: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`, href: `https://www.youtube.com/watch?v=${yt[1]}` };
  if (/vimeo\.com\/\d+/.test(url)) {
    try {
      const r = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`);
      const j = await r.json();
      return { thumb: j.thumbnail_url || null, href: url };
    } catch { return { thumb: null, href: url }; }
  }
  return null;
}

export function RichTextEditor({ value, onChange, placeholder, minHeight = 180, testId = "rich-text-editor", enableVideo = false }) {
  const quillRef = useRef(null);

  const imageHandler = useCallback(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg,image/webp,image/gif";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 2_500_000) { toast.error("Afbeelding mag maximaal 2,5 MB zijn"); return; }
      try {
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(",")[1]);
          r.onerror = rej;
          r.readAsDataURL(file);
        });
        const { data } = await api.post("/mail-assets/upload", { content: b64, content_type: file.type, filename: file.name });
        const range = quill.getSelection(true) || { index: quill.getLength() };
        quill.insertEmbed(range.index, "image", data.url, "user");
        quill.setSelection(range.index + 1, 0);
      } catch (e) { toast.error(e.response?.data?.detail || "Afbeelding uploaden mislukt"); }
    };
    input.click();
  }, []);

  const videoHandler = useCallback(async () => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const url = window.prompt("Plak een YouTube- of Vimeo-link:");
    if (!url) return;
    const v = await _resolveVideo(url.trim());
    if (!v) { window.alert("Geen geldige YouTube- of Vimeo-link herkend."); return; }
    const range = quill.getSelection(true) || { index: quill.getLength() };
    if (v.thumb) {
      quill.insertEmbed(range.index, "image", v.thumb, "user");
      quill.setSelection(range.index, 1);
      quill.format("link", v.href);
      quill.setSelection(range.index + 1, 0);
    } else {
      quill.insertText(range.index, "▶ Bekijk de video", "link", v.href, "user");
      quill.setSelection(range.index + 17, 0);
    }
  }, []);

  const ctaHandler = useCallback(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const text = window.prompt("Tekst op de knop:", "Bevestig je komst");
    if (!text) return;
    const url = window.prompt("Waar linkt de knop naartoe? (volledige URL)", "https://");
    if (!url || url.trim() === "https://") return;
    const range = quill.getSelection(true) || { index: quill.getLength() };
    quill.insertText(range.index, text, { ctabtn: url.trim() }, "user");
    quill.insertText(range.index + text.length, " ", { ctabtn: false }, "user");
    quill.setSelection(range.index + text.length + 1, 0);
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [false, 2, 3] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        enableVideo ? ["link", "image", "ctabtn", "blockquote", "video"] : ["link", "image", "ctabtn", "blockquote"],
        ["clean"],
      ],
      handlers: enableVideo
        ? { video: videoHandler, image: imageHandler, ctabtn: ctaHandler }
        : { image: imageHandler, ctabtn: ctaHandler },
    },
  }), [enableVideo, videoHandler, imageHandler, ctaHandler]);

  return (
    <div data-testid={testId} className="rte" style={{ "--rte-min": `${minHeight}px` }}>
      <ReactQuill ref={quillRef} theme="snow" value={value || ""} onChange={onChange}
        modules={modules} formats={FORMATS} placeholder={placeholder} />
    </div>
  );
}
